/**
 * BonBon Bar Pick & Mix cart attribute controller.
 * Does not alter or remove accelerated checkout buttons; validation protects the ordinary button.
 * Compatible with dynamically re-rendered Horizon cart drawer markup.
 */
(() => {
  if (customElements.get('bb-pick-mix-packing')) return;

  const MIXED = 'Tout mélanger dans un seul sac';
  const SEPARATE = 'Emballer chaque variété séparément';
  const VALID = [MIXED, SEPARATE];
  const root = () => window.Shopify?.routes?.root || '/';
  const endpoint = (part) => root() + part;

  class PickMixPacking extends HTMLElement {
    static get observedAttributes() { return ['data-has-pick-mix', 'data-enabled']; }

    constructor() {
      super();
      this.serialWrites = Promise.resolve();
      this.revision = 0;
      this.saving = false;
      this.failedValue = null;
      this.confirmed = null;
      this.onChange = this.onChange.bind(this);
      this.onRadioClick = this.onRadioClick.bind(this);
      this.onCheckoutClick = this.onCheckoutClick.bind(this);
      this.onCheckoutSubmit = this.onCheckoutSubmit.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    get active() {
      return this.dataset.enabled === 'true' && this.dataset.hasPickMix === 'true';
    }

    get key() { return this.dataset.attributeName; }
    get radios() { return Array.from(this.querySelectorAll('input[type="radio"]')); }
    get choice() { return this.radios.find((radio) => radio.checked)?.value || null; }

    connectedCallback() {
      // Liquid supplies immediate server-rendered state. The cart fetch reconciles it.
      this.confirmed = VALID.includes(this.dataset.currentValue) ? this.dataset.currentValue : null;
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onRadioClick);
      document.addEventListener('click', this.onCheckoutClick, true);
      document.addEventListener('submit', this.onCheckoutSubmit, true);
      document.addEventListener('cart:updated', this.onCartUpdate);
      window.addEventListener('pageshow', this.onPageShow);
      this.syncFromCart();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onRadioClick);
      document.removeEventListener('click', this.onCheckoutClick, true);
      document.removeEventListener('submit', this.onCheckoutSubmit, true);
      document.removeEventListener('cart:updated', this.onCartUpdate);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    attributeChangedCallback() {
      if (!this.isConnected) return;
      this.syncFromCart();
    }

    async getCart() {
      const response = await fetch(endpoint('cart.js'), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cannot retrieve cart');
      return response.json();
    }

    async setAttributeValue(value) {
      const response = await fetch(endpoint('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [this.key]: value } }),
      });
      if (!response.ok) throw new Error('Cannot save packing choice');
      const cart = await response.json();
      // Shopify sometimes removes empty attributes rather than returning the empty string.
      if ((cart.attributes?.[this.key] || '') !== value) {
        throw new Error('Packing choice not confirmed by cart');
      }
      return cart;
    }

    async syncFromCart() {
      const version = this.revision;
      try {
        const cart = await this.getCart();
        if (!this.isConnected || version !== this.revision) return;
        const saved = cart.attributes?.[this.key] || '';
        if (!this.active) {
          if (saved) await this.clearStaleAttribute();
          return;
        }
        if (this.saving) return;
        this.confirmed = VALID.includes(saved) ? saved : null;
        this.radios.forEach((radio) => { radio.checked = radio.value === this.confirmed; });
        this.hideError();
      } catch (error) {
        // Retain Liquid-rendered state if cart cannot be fetched.
      }
    }

    async clearStaleAttribute() {
      // Serialise against any in-flight selection from this instance.
      this.revision++;
      this.serialWrites = this.serialWrites.catch(() => {}).then(() => this.setAttributeValue(''));
      try { await this.serialWrites; } catch (error) { /* retry on next render */ }
    }

    onChange(event) {
      if (!this.active || !(event.target instanceof HTMLInputElement)) return;
      if (event.target.type !== 'radio' || !this.contains(event.target)) return;
      this.saveChoice(event.target.value);
    }

    onRadioClick(event) {
      // Clicking the same checked radio after a network failure should retry saving.
      if (!this.active || !(event.target instanceof HTMLInputElement)) return;
      if (event.target.type === 'radio' && event.target.checked && this.failedValue === event.target.value && !this.saving) {
        this.saveChoice(event.target.value);
      }
    }

    saveChoice(value) {
      if (!VALID.includes(value)) return;
      this.revision++;
      const revision = this.revision;
      this.saving = true;
      this.confirmed = null;
      this.failedValue = null;
      this.hideError();
      this.serialWrites = this.serialWrites.catch(() => {}).then(() => this.setAttributeValue(value));
      this.serialWrites.then(() => {
        if (!this.isConnected || revision !== this.revision) return;
        this.saving = false;
        this.confirmed = value;
        this.hideError();
      }).catch(() => {
        if (!this.isConnected || revision !== this.revision) return;
        this.saving = false;
        this.confirmed = null;
        this.failedValue = value;
        this.showError(this.dataset.saveError);
      });
    }

    showError(message) {
      const error = this.querySelector('.bb-pick-mix__error');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = false;
    }

    hideError() {
      const error = this.querySelector('.bb-pick-mix__error');
      if (error) error.hidden = true;
    }

    blockCheckout(event) {
      if (!this.active) return false;
      if (this.choice && !this.saving && this.choice === this.confirmed) return false;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.saving) this.showError(this.dataset.pendingError);
      else if (this.failedValue) this.showError(this.dataset.saveError);
      else this.showError(this.dataset.requiredError);
      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (!this.choice) this.radios[0]?.focus({ preventScroll: true });
      return true;
    }

    onCheckoutClick(event) {
      // Only the ordinary checkout button associated with this drawer.
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button[name="checkout"],input[name="checkout"],a[href*="/checkout"]');
      if (!button || !document.querySelector('#cart-drawer')?.contains(button)) return;
      if (button.closest('.additional-checkout-buttons, [data-shopify="payment-button"]')) return;
      this.blockCheckout(event);
    }

    onCheckoutSubmit(event) {
      const button = event.submitter;
      if (button && document.querySelector('#cart-drawer')?.contains(button) && button.name === 'checkout') {
        this.blockCheckout(event);
      }
    }

    onCartUpdate() { this.syncFromCart(); }
    onPageShow(event) { if (event.persisted) this.syncFromCart(); }
  }

  customElements.define('bb-pick-mix-packing', PickMixPacking);
})();
